import React, { memo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '@easyflow/ui-frontend/components/ui/avatar';
import { aurora } from '@/components/aurora/tokens';
import { Popover, PopoverContent, PopoverTrigger } from '@easyflow/ui-frontend/components/ui/popover';
import { Check, CheckCheck, Pin, Reply, Smile, MoreHorizontal, FileText, Star, Forward, ListTodo, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatMessageTime, getAvatarGradient, getInitials, getSenderColor, MENTION_RE, firstUrl, EMOJI_LIST } from '../utils/format';
import type { ChatDensity, ChatMessage, MessageAttachment } from '../types';
import { LinkPreviewCard } from './LinkPreviewCard';
import { VoicePlayer } from './VoicePlayer';
import { EditHistoryTooltip } from './EditHistoryTooltip';
import { EntityCard } from '@/components/cross/EntityCard';
import { ENTITY_MENTION_RE, parseEntityMentions } from '@/lib/cross-bridge';
import { storageFileUrl } from '@/lib/storage-url';

interface Props {
  msg: ChatMessage;
  isMine: boolean;
  isContinuation: boolean;
  isGroup: boolean;
  density: ChatDensity;
  currentUserId: string | null | undefined;
  index?: number;
  onReply: (m: ChatMessage) => void;
  onForward: (m: ChatMessage) => void;
  onReact: (messageId: string, emoji: string) => void;
  onPin: (messageId: string, isPinned: boolean) => void;
  onFavorite: (messageId: string, isFav: boolean) => void;
  onCreateTask: (m: ChatMessage) => void;
  onDelete: (messageId: string) => void;
}

function parseAttachments(att: ChatMessage['attachments']): MessageAttachment[] {
  if (!att) return [];
  if (Array.isArray(att)) return att;
  if (typeof att === 'string') {
    try { const parsed = JSON.parse(att); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
  }
  return [];
}

function resolveUrl(att: MessageAttachment): string {
  // Task #4040 — serving autenticato: token in query (gli <img>/<a> non
  // inviano l'Authorization header), ownership check lato backend.
  return storageFileUrl(att.cloud_path || att.url);
}

// Task #1660 — render content con mention highlight + URL detection. Tutto
// inline per evitare re-render: parseMentionContent ritorna ReactNode[].
function renderRichContent(text: string | null, myId: string | null | undefined): React.ReactNode {
  if (!text) return null;
  const re = new RegExp(MENTION_RE.source, 'g');
  const out: React.ReactNode[] = [];
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(<span key={`t-${key++}`}>{text.slice(last, m.index)}</span>);
    const isMe = m[2] === myId;
    out.push(
      <span
        key={`m-${key++}`}
        className={cn(
          'inline-flex items-baseline px-1 -mx-0.5 rounded font-semibold',
          isMe
            ? 'bg-yellow-200/80 dark:bg-yellow-500/30 text-yellow-900 dark:text-yellow-100 ring-1 ring-yellow-400/40'
            : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200',
        )}
      >
        @{m[1]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(<span key={`t-${key++}`}>{text.slice(last)}</span>);
  return out;
}

function MessageItemInner(props: Props) {
  const {
    msg, isMine, isContinuation, isGroup, density, currentUserId, index,
    onReply, onForward, onReact, onPin, onFavorite, onCreateTask, onDelete,
  } = props;
  const senderName = `${msg.sender_first_name || ''} ${msg.sender_last_name || ''}`.trim() || 'Utente';
  const atts = parseAttachments(msg.attachments);
  const isVoice = msg.message_type === 'voice' || atts.some((a) => a.kind === 'voice');
  const linkUrl = firstUrl(msg.content);
  const mentionedMe = !!(msg.mentions && currentUserId && msg.mentions.includes(currentUserId));

  const padding = density === 'compact' ? 'px-3 py-1' : 'px-3.5 py-2';
  const gap = density === 'compact' ? 'mt-px' : isContinuation ? 'mt-0.5' : 'mt-3';
  // Task #1665 — entry soffusa per messaggi nuovi; rispetta reduced-motion.
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      role="listitem"
      initial={reduceMotion ? false : aurora.listItemIn.initial}
      animate={aurora.listItemIn.animate}
      transition={{ ...aurora.listItemIn.transition, delay: reduceMotion ? 0 : aurora.stagger(index ?? 0) }}
      className={cn('flex gap-2 group/msg', isMine ? 'flex-row-reverse' : '', gap)}>
      {!isMine && !isContinuation && (
        <Avatar className="h-8 w-8 shrink-0 mt-0.5 ring-1 ring-white dark:ring-gray-800 shadow-sm">
          <AvatarImage src={msg.sender_avatar_url || undefined} />
          <AvatarFallback className={`bg-gradient-to-br ${getAvatarGradient(senderName)} text-white text-[10px] font-bold`}>
            {getInitials(msg.sender_first_name, msg.sender_last_name)}
          </AvatarFallback>
        </Avatar>
      )}
      {!isMine && isContinuation && <div className="w-8 shrink-0" />}

      <div className={cn('max-w-[75%] relative', isMine && 'flex flex-col items-end')}>
        {(msg.reply_content || msg.reply_to_id) && msg.reply_content && (
          <div className={cn(
            'text-[10px] px-2.5 py-1 rounded-lg mb-1 border-l-2',
            isMine ? 'bg-blue-500/20 text-blue-100 border-blue-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 border-gray-300 dark:border-gray-600',
          )}>
            <span className="font-semibold">{msg.reply_sender_name}</span>: {msg.reply_content.substring(0, 80)}
          </div>
        )}

        <div className={cn(
          'shadow-sm',
          padding,
          isMine
            ? 'bg-blue-600 text-white rounded-2xl rounded-tr-sm'
            : 'bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl rounded-tl-sm',
          mentionedMe && !isMine && 'ring-2 ring-yellow-400/70 dark:ring-yellow-500/50',
        )}>
          {isGroup && !isMine && !isContinuation && (
            <p className={cn('text-[11px] font-semibold mb-0.5', getSenderColor(msg.sender_id || ''))}>
              {senderName}
            </p>
          )}

          {isVoice && atts[0] ? (
            <VoicePlayer
              src={resolveUrl(atts[0])}
              durationMs={atts[0].duration_ms ?? null}
              isMine={isMine}
            />
          ) : (
            <>
              {msg.content && (
                <p className="text-sm whitespace-pre-wrap leading-relaxed break-words">
                  {renderRichContent(msg.content, currentUserId)}
                </p>
              )}

              {msg.content && parseEntityMentions(msg.content).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {parseEntityMentions(msg.content).map((em, i) => (
                    <EntityCard key={`em-${i}-${em.id}`} kind={em.kind} id={em.id} compact />
                  ))}
                </div>
              )}

              {linkUrl && <LinkPreviewCard url={linkUrl} isMine={isMine} />}

              {atts.length > 0 && (
                <div className="mt-1.5 space-y-1">
                  {atts.map((att, i) => {
                    const url = resolveUrl(att);
                    if (att.mime_type?.startsWith('image/')) {
                      return (
                        <img
                          key={i}
                          src={url}
                          alt={att.filename}
                          loading="lazy"
                          className="max-w-full rounded-lg max-h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => window.open(url, '_blank')}
                        />
                      );
                    }
                    return (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className={cn(
                          'flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs',
                          isMine ? 'bg-blue-700/50 text-blue-100 hover:bg-blue-700/70' : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600',
                        )}
                      >
                        <FileText className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{att.filename}</span>
                      </a>
                    );
                  })}
                </div>
              )}
            </>
          )}

          <div className={cn('flex items-center gap-1 mt-1', isMine ? 'justify-end' : 'justify-start')}>
            <span className={cn('text-[10px]', isMine ? 'text-blue-200' : 'text-gray-400')}>
              {formatMessageTime(msg.created_at)}
            </span>
            {msg.is_edited && (
              <span className={cn(isMine ? 'text-blue-200' : 'text-gray-400')}>
                <EditHistoryTooltip messageId={msg.id} editedAt={msg.edited_at} />
              </span>
            )}
            {isMine && (
              (msg.read_by_count || 0) > 0
                ? <CheckCheck className="h-3 w-3 text-blue-300" />
                : <Check className="h-3 w-3 text-blue-300" />
            )}
            {msg.is_pinned && <Pin className="h-2.5 w-2.5 text-amber-400 rotate-45" />}
          </div>
        </div>

        {msg.reactions && msg.reactions.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {Object.entries(msg.reactions.reduce<Record<string, number>>((acc, r) => {
              acc[r.emoji] = (acc[r.emoji] || 0) + 1;
              return acc;
            }, {})).map(([emoji, count]) => (
              <button
                key={emoji}
                onClick={() => onReact(msg.id, emoji)}
                className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-xs transition-colors border border-gray-200 dark:border-gray-600"
              >
                {emoji} <span className="font-medium text-[10px]">{count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={cn(
        'flex items-center gap-0.5 opacity-0 group-hover/msg:opacity-100 transition-opacity self-center shrink-0',
        isMine ? 'flex-row-reverse' : '',
      )}>
        <Popover>
          <PopoverTrigger asChild>
            <button className="h-6 w-6 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center" aria-label="Reazione">
              <Smile className="h-3.5 w-3.5 text-gray-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" side={isMine ? 'left' : 'right'}>
            <div className="grid grid-cols-6 gap-1">
              {EMOJI_LIST.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => onReact(msg.id, emoji)}
                  className="h-7 w-7 rounded hover:bg-accent flex items-center justify-center text-sm hover:scale-125 transition-transform"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        <button onClick={() => onReply(msg)} className="h-6 w-6 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center" aria-label="Rispondi">
          <Reply className="h-3.5 w-3.5 text-gray-400" />
        </button>
        <Popover>
          <PopoverTrigger asChild>
            <button className="h-6 w-6 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center" aria-label="Altro">
              <MoreHorizontal className="h-3.5 w-3.5 text-gray-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-44 p-1" side={isMine ? 'left' : 'right'}>
            <button onClick={() => onPin(msg.id, msg.is_pinned)} className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-accent rounded">
              <Pin className="h-3 w-3" /> {msg.is_pinned ? 'Rimuovi pin' : 'Fissa'}
            </button>
            <button onClick={() => onFavorite(msg.id, !!msg.is_favorited)} className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-accent rounded">
              <Star className="h-3 w-3" /> {msg.is_favorited ? 'Rimuovi preferito' : 'Preferito'}
            </button>
            <button onClick={() => onForward(msg)} className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-accent rounded">
              <Forward className="h-3 w-3" /> Inoltra
            </button>
            <button onClick={() => onCreateTask(msg)} className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-accent rounded">
              <ListTodo className="h-3 w-3" /> Crea task
            </button>
            {isMine && (
              <button onClick={() => onDelete(msg.id)} className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-accent rounded text-red-600">
                <Trash2 className="h-3 w-3" /> Elimina
              </button>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </motion.div>
  );
}

export const MessageItem = memo(MessageItemInner, (prev, next) => {
  // Re-render solo se il messaggio cambia o se cambia il flag isContinuation
  // / density (riduce drasticamente il costo della virtuoso list su scroll).
  return (
    prev.msg === next.msg &&
    prev.isMine === next.isMine &&
    prev.isContinuation === next.isContinuation &&
    prev.isGroup === next.isGroup &&
    prev.density === next.density &&
    prev.currentUserId === next.currentUserId
  );
});
